package fr.arcplex.sooner.coherence

import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.RailItem

/**
 * Renderer-neutral burger-menu model.
 *
 * Both the legacy menu view and the Coherence menu view render this model: the
 * legacy payload is a [Rail] of [RailItem]s, and a future `/api/box/nav`
 * response only needs another mapper to feed the same views.
 */
data class CoherenceNavItem(
    val uid: String,
    val label: String,
    val icon: String?,
    val actionType: String?,
    val actionUrl: String?,
    val width: Float,
    val height: Float
) {
    val isImage: Boolean get() = !icon.isNullOrBlank()

    /** Items without an action are spacers or section gaps: never clickable. */
    val hasAction: Boolean get() = !actionUrl.isNullOrBlank() || !actionType.isNullOrBlank()
}

data class CoherenceNav(
    val title: String = "",
    val items: List<CoherenceNavItem> = emptyList()
)

/**
 * Maps the server menu rail to the neutral model. Returns null when there is
 * nothing to render, so callers can fall back to "no menu" without checks.
 */
fun coherenceNavFrom(menu: Rail?): CoherenceNav? {
    val items = menu?.items.orEmpty().map { item ->
        val isImage = item.type == "image"
        CoherenceNavItem(
            uid = item.uid,
            // Menu text rows display the object label; content is the payload.
            label = item.label ?: if (isImage) "" else item.content,
            icon = if (isImage) item.content.takeIf { it.isNotBlank() } else null,
            actionType = item.action?.type,
            actionUrl = item.action?.url,
            width = item.width,
            height = item.height
        )
    }
    if (items.isEmpty()) return null
    return CoherenceNav(title = menu?.label.orEmpty(), items = items)
}
